import {
  ClockCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { ReactComponent as MoneyOutlined } from 'assets/icons/money.svg';
import { AccordionAPI, AccordionItem } from '@mm/components';
import { useAudiencesCRUDActions } from '@mm/state/actions/AudiencesCRUDActions';
import AudiencesModel from '@mm/state/model/AudiencesModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { Tooltip } from 'antd';
import classNames from 'classnames';
import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { IntegrationIcon } from '../../Integrations/IntegrationIcon/IntegrationIcon';
import { Audience } from '../Audience/Audience';
import { getESStructure } from '../Audiences';
import { useAudienceViewHandlers } from '../use-audience-view-handlers';
import { useLeadContactsFilter } from '../use-lead-contacts-filter';
import styles from './AudienceAccordionItem.module.css';

export const AudienceAccordionItem = ({
  audience,
  processClick,
  fetchContacts,
  activeAudienceId,
  busy,
}: {
  audience: AudiencesModel;
  processClick: (onSuccess: (args: any) => void) => void;
  fetchContacts: (
    pageSize?: number,
    pageNumber?: number,
    query?: string,
  ) => void;
  activeAudienceId?: number;
  busy?: boolean;
}) => {
  const activeChatbotId = useSelector(activeChatbotIdSelector);
  const isPurchasedOrESLeadsAudience =
    audience?.type === 'leads_db' || audience?.type === 'es_based';
  const audienceNotReady = audience.status === 'not_ready';
  const audiencesSorting = useSelector(
    (state: StoreState) => state.audiences.sorting,
  );
  const convertedLeadsAudience = audience.type === 'converted_leads';

  const { setAudienceId } = useLeadContactsFilter();
  const { onDelete } = useAudienceViewHandlers();
  const { openCreateMyLeadsAudienceModal } = useModalActions();
  const { update } = useAudiencesCRUDActions();

  const ref = useRef<AccordionAPI>(null);

  const scrollToElement = () => {
    if (activeAudienceId) {
      document
        .getElementById(activeAudienceId?.toString())
        ?.scrollIntoView(true);
    }
  };

  useEffect(() => scrollToElement(), [activeAudienceId]);

  const onUpdateAudienceName = async (audienceName: string) => {
    await update(
      {
        ...audience,
        esFilters: getESStructure(audience?.esFilters),
        name: audienceName,
      },
      activeChatbotId,
    );
  };

  const onEditClick = (e: React.MouseEvent<HTMLSpanElement>) => {
    e.stopPropagation();
    openCreateMyLeadsAudienceModal({
      title: 'Name your Audience',
      defaultName: audience?.name,
      onSave: onUpdateAudienceName,
    });
  };

  const onDeleteClick = (e: React.MouseEvent<HTMLSpanElement>) => {
    e.stopPropagation();
    onDelete({
      audienceId: audience.id,
      audienceName: audience?.name || '',
      onSuccess: () => setAudienceId(undefined),
      options: {
        inUse: true,
        sortColumn: audiencesSorting?.sortColumn,
        sortDirection: audiencesSorting?.sortDirection,
      },
    });
  };

  const navigateToAudience = (activeAudienceId: number | undefined) => {
    setAudienceId(activeAudienceId);
  };

  const title = (
    <div className={styles.titleContainer}>
      <div className={styles.titleRow}>
        <div className={styles.audienceNameContainer}>
          {audienceNotReady && (
            <div className={styles.alert}>
              <ClockCircleOutlined />
              Populating your audience may take up to 15 minutes.
            </div>
          )}
          <span
            className={classNames(styles.title, {
              [styles.titleInactive]: audienceNotReady,
            })}
          >
            {audience.name}
          </span>
          {!audienceNotReady && (
            <>
              {!convertedLeadsAudience && (
                <EditOutlined
                  disabled={!activeAudienceId}
                  onClick={e => processClick(() => onEditClick(e))}
                />
              )}

              <DeleteOutlined
                onClick={e => onDeleteClick(e)}
                disabled={!activeAudienceId || isPurchasedOrESLeadsAudience}
              />
            </>
          )}
        </div>
        {audience.id !== activeAudienceId && (
          <div className={styles.integrationsContainer}>
            {audience.integrationTypes?.map(type => (
              <IntegrationIcon key={type} integrationType={type} isActive />
            ))}
          </div>
        )}
      </div>
      <div className={styles.titleContent}>
        {convertedLeadsAudience && (
          <div className={styles.convertedLeadsAudLogo}>
            <MoneyOutlined className={styles.contactsIcon} />
            <span style={{ fontWeight: 500 }}>Converted Leads</span>
          </div>
        )}
        {audience.id !== activeAudienceId && !!audience.automations?.length && (
          <Tooltip
            overlayClassName={styles.tooltip}
            title={audience.automations.map(item => (
              <div key={item[0]} className={styles.automationName}>
                {item[1]}
              </div>
            ))}
          >
            <div>
              Automations:{' '}
              <span style={{ fontWeight: 500 }}>
                {audience.automations?.length}
              </span>
            </div>
          </Tooltip>
        )}

        <div className={styles.contacts}>
          <TeamOutlined className={styles.contactsIcon} />
          <span
            className={classNames(styles.contactsCount, {
              [styles.titleInactive]: audienceNotReady,
            })}
          >
            {audience.contactsCount}
          </span>
        </div>
      </div>
    </div>
  );

  useEffect(() => {
    if (audience.id === activeAudienceId) {
      ref.current?.open();
    } else {
      ref.current?.close();
    }
  }, [activeAudienceId]);

  return (
    <div className={styles.wrapper} id={audience.id.toString()}>
      <AccordionItem
        ref={ref}
        titleComponent={title}
        className={classNames(styles.accordionItem, {
          [styles.convertedLeadsAudience]: convertedLeadsAudience,
        })}
        onToggle={(opened: boolean) => {
          if (opened) {
            navigateToAudience(audience.id);
          } else {
            setAudienceId(undefined);
          }
        }}
        closeOnOutsideClick={false}
        caretStyleIcon
      >
        <div className={styles.content}>
          <Audience
            fetchContacts={fetchContacts}
            audience={audience}
            activeAudienceId={activeAudienceId}
            processClick={processClick}
            busy={busy}
          />
        </div>
      </AccordionItem>
    </div>
  );
};
